import {Component, EventEmitter, Input, Output} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import {NgClass, NgStyle} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {InformationCardComponent} from './information/information-card.component';
import {
  PendingPatientsAlertService
} from '../../../core/services/pending-patients-alert/location/pending-patients-alert.service';
import {SnackbarCustomService} from '../snackbar/snackbar-custom.service';
import {DetailsModalAlert} from './details-modal/details-modal-alert.component';

@Component({
  selector: 'pending-alerts-card',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCardModule,
    MatListModule,
    MatPaginatorModule,
    MatProgressBarModule,
    NgStyle,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    NgClass,
  ],
  templateUrl: './pending-alerts-card.component.html',
  styleUrls: ['./pending-alerts-card.component.scss'],
})
export class PendingAlertsCardComponent {
  constructor(
    private service: PendingPatientsAlertService,
    private snackbar: SnackbarCustomService,
    private dialog: MatDialog,
  ) {}

  @Output() response = new EventEmitter();
  @Output() accessModal = new EventEmitter();
  @Output() closeModal = new EventEmitter();

  @Input() alert!: {
    expanded: boolean;
    contexto_nome: string;
    respondido_por_id: number | null;
    observacoes: string | null;
    respondido_por_nome: string | null;
    respondido_em: string | null;
    classificacao_id: number;
    alerta_id: number;
    medicao_id: number;
    valor_medido: number;
    nivel_acionado: number;
    criado_em: string;
    classificacao: string;
    paciente_id: number;
    paciente_nome: string;
    telefone_whatsapp: string;
    unidade_nome?: string;
    tempo_resposta_max?: number;
    minutos_passados: number;
    status_tempo: string;
  };

  loading_tutor: boolean = false;
  loading_wpp: boolean = false;
  loading_finalizar: boolean = false;

  /**
   * Retorna array de letras da classificação para exibição vertical
   */
  letrasClassificacao(): any[] {
    return (this.alert?.classificacao || '').split('');
  }

  /**
   * Gera aria-label descritivo para o card completo
   */
  getCardAriaLabel(): string {
    const status = this.alert.respondido_em ? 'finalizado' : 'pendente';
    const classificacao = this.getClassificacaoTexto();
    return `Alerta ${status} de ${classificacao} para paciente ${this.alert.paciente_nome}, ` +
      `glicemia ${this.alert.valor_medido} mg/dL, ` +
      `gerado em ${this.formatarDataParaLeitura(this.alert.criado_em)}`;
  }

  /**
   * Gera aria-label para a coluna de classificação
   */
  getClassificacaoAriaLabel(): string {
    return `Classificação: ${this.getClassificacaoTexto()}`;
  }

  /**
   * Retorna texto da classificação baseado no ID
   */
  getClassificacaoTexto(): string {
    const map: { [key: number]: string } = {
      1: 'Leve',
      2: 'Moderado',
      3: 'Crítico',
      4: 'Crítico severo'
    };
    return map[this.alert.classificacao_id] || this.alert.classificacao;
  }

  /**
   * Formata data para leitura natural por leitores de tela
   */
  formatarDataParaLeitura(dataISO: string): string {
    const [data, hora] = dataISO.split(' ');
    const [ano, mes, dia] = data.split('-');
    return `${dia} de ${this.getMesNome(parseInt(mes))} de ${ano} às ${hora}`;
  }

  /**
   * Retorna nome do mês por extenso
   */
  getMesNome(mes: number): string {
    const meses = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    return meses[mes - 1] || '';
  }

  /**
   * Gerencia navegação por teclado no card
   */
  handleKeyDown(event: KeyboardEvent): void {
    // Se pressionar Enter ou Espaço no card, abre detalhes
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openModal(this.alert);
    }

    // Esc fecha overlay se estiver aberto
    if (event.key === 'Escape' && this.alert.expanded) {
      event.preventDefault();
      this.alert.expanded = false;
    }
  }

  /**
   * Aciona tutor do paciente via WhatsApp
   */
  acionartutorlocal(id: number): void {
    this.loading_tutor = true;
    this.announceAction('Acionando tutor do paciente');

    this.service.contactHealthAgent(id).subscribe({
      next: (res: any) => {
        this.openWhatsappAgenteComMaps(res.data);
        this.announceAction('Tutor acionado com sucesso');
        this.response.emit(true);
      },
      error: () => {
        this.announceAction('Erro ao acionar tutor');
        this.response.emit(false);
        this.loading_tutor = false;
      },
      complete: () => {
        this.loading_tutor = false;
      }
    });
  }

  /**
   * Abre WhatsApp do agente com localização do paciente
   */
  openWhatsappAgenteComMaps(data: any): void {
    const e = data.endereco;
    const enderecoTexto =
      `${e.logradouro}, ${e.numero}` +
      `${e.complemento ? ', ' + e.complemento : ''}, ` +
      `${e.bairro}, ${e.cidade} - ${e.estado}`;

    const enderecoMaps = encodeURIComponent(
      `${e.logradouro} ${e.numero} ${e.bairro} ${e.cidade} ${e.estado}`
    );
    const linkMaps = `https://www.google.com/maps/search/?api=1&query=${enderecoMaps}`;

    const mensagem =
      `Olá ${data.tutor.nome_tutor}, esta é a central do Glicosus.\n` +
      `O paciente ${data.nome_paciente} (telefone: +${data.telefone_paciente}),\n` +
      `está com glicemia ${data.valor_medido} mg/dL.\n\n` +
      `Endereço do paciente:\n${enderecoTexto}\n\n` +
      `*Localização no Google Maps:*\n${linkMaps}\n\n` +
      `Por favor, se dirija ao local do paciente e nos dê retorno de novas informações.`;

    const telefone = data.tutor.whatsapp_tutor || data.tutor.telefone_tutor;
    const url = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }

  /**
   * Abre conversa do WhatsApp com o paciente
   */
  whatsapppaciente(id: number): void {
    this.loading_wpp = true;
    this.announceAction('Abrindo conversa no WhatsApp');

    this.service.openChat(id).subscribe({
      next: (res: any) => {
        const paciente = res.data;
        this.openWhatsapp(
          paciente.telefone_whatsapp,
          paciente.nome,
          paciente.valor_medido
        );
        this.announceAction('Conversa do WhatsApp aberta');
        this.response.emit(true);
      },
      error: () => {
        this.announceAction('Erro ao abrir WhatsApp');
        this.response.emit(false);
        this.loading_wpp = false;
      },
      complete: () => {
        this.loading_wpp = false;
      }
    });
  }

  /**
   * Abre modal com detalhes do alerta
   */
  openModal(alert: typeof this.alert): void {
    this.accessModal.emit(true);

    const dialogRef = this.dialog.open(DetailsModalAlert, {
      enterAnimationDuration: '150ms',
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      exitAnimationDuration: '150ms',
      data: alert,
      panelClass: 'modal-alerta-dialog',
      ariaLabel: `Detalhes do alerta de ${alert.paciente_nome}`
    });

    dialogRef.componentInstance.response.subscribe((response: any) => {
      if (response === 'close') {
        this.closeModal.emit(true);
      }
    });
  }

  /**
   * Abre WhatsApp do paciente
   */
  openWhatsapp(telefone: string, nome: string, valor: number): void {
    const mensagem =
      `Olá ${nome}, esta é a central do Glicosus, ` +
      `estamos entrando em contato para avisar que a sua glicemia está alterada ` +
      `(${valor} mg/dL).`;

    const url = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }

  /**
   * Finaliza alerta
   */
  finalizar(id: number): void {
    this.announceAction('Abrindo formulário de finalização');

    const dialogRef = this.dialog.open(InformationCardComponent, {
      enterAnimationDuration: '100ms',
      width: '450px',
      height: '300px',
      exitAnimationDuration: '100ms',
      data: id,
      ariaLabel: 'Formulário de finalização do alerta'
    });

    dialogRef.componentInstance.response.subscribe((response: any) => {
      if (response === true) {
        this.snackbar.success('Alerta finalizado com sucesso.');
        this.announceAction('Alerta finalizado com sucesso');
        this.response.emit(true);
      }
      if (response === 'false') {
        this.snackbar.error('Não foi possível finalizar esse alerta, entre em contato com o suporte.');
        this.announceAction('Erro ao finalizar alerta');
        this.response.emit(true);
      }
    });
  }

  /**
   * Anuncia ações para leitores de tela
   * Cria elemento aria-live temporário
   */
  private announceAction(message: string): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}
